// --- Dependencies -------------------------------------------------------------------------------
import { useRef } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import usePopup from "@/hooks/usePopup";
import useStore from "@/hooks/useStore";
import SwitchStorePopup from "@/UI/popups/SwitchStorePopup";
import "./styles.css";

// --- Logic --------------------------------------------------------------------------------------

/**
 * The active store badge that opens the store picker. Shared by the sidebar and the mobile tabs
 * sheet so both surfaces show the same store, the same way — one account can hold several stores and
 * knowing which one you are editing matters before you touch anything.
 *
 * It opens even for a client with a single store: the picker also carries the "create a new store"
 * action, which would otherwise be unreachable from inside the dashboard.
 */
export default function StoreSwitcher() {
    const lang = useLang((state) => state.lang);
    const store = useStore((state) => state.store);
    const { addComp, removeComp } = usePopup();
    const badgeRef = useRef<HTMLButtonElement>(null);

    const storeName = store?.name.find((n) => n.lang === (lang ?? "ar"))?.value ?? store?.name[0]?.value ?? "";

    const handleSwitchStore = () => {
        const rect = badgeRef.current?.getBoundingClientRect();
        if (!rect) return;
        const name = "switch-store";
        removeComp(name);
        addComp({
            name,
            Component: SwitchStorePopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            coverClose: true,
            anchor: { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height },
        });
    };

    return (
        <button ref={badgeRef} type="button" className="store-switcher" onClick={handleSwitchStore}>
            <div className="store-switcher-logo">
                {store?.logo ? <img src={store.logo} alt={storeName} /> : <span className="store-switcher-logo-fallback">{storeName.charAt(0)}</span>}
            </div>
            <p className="store-switcher-name">{storeName}</p>
            <img className="store-switcher-arrow" src="/icons/arrow-orange-icon.svg" alt="" />
        </button>
    );
}
